import React, { useEffect, useState } from "react";
import axios from "axios"; // Import axios

const GradedEssayData = ({ essayData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [activePage, setActivePage] = useState(0);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [editedFeedback, setEditedFeedback] = useState("");
  const [highlightControls, setHighlightControls] = useState({
    topPriority: false,
    mediumPriority: false,
    lowPriority: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [hoveredFeedbackIndex, setHoveredFeedbackIndex] = useState(null);
  const [enableHighlighting, setEnableHighlighting] = useState(false);
  const [isAddFeedbackModalOpen, setIsAddFeedbackModalOpen] = useState(false);
  const [newFeedback, setNewFeedback] = useState({
    text: '',
    priority: ''
  });
  const [feedBackState, setFeedBackState] = useState(essayData);

  const [feedbackList, setFeedbackList] = useState([]);

  useEffect(() => {
    setFeedbackList(essayData.question_responses[0]?.pages[0]?.feedback || []);
  }, [essayData]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);


  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setActivePage(0);
  };

  const handlePrevPage = () => {
    setActivePage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = (totalPages) => {
    setActivePage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleEditFeedback = (feedback, index) => {
    setEditingFeedback(index);
    setEditedFeedback(feedback);
  };

  const handleSaveFeedback = (index) => {
    if (!editedFeedback.trim()) {
      return; // Don't save empty feedback
    }

    setFeedbackList(prevList => 
      prevList.map((item, i) => 
        i === index 
          ? { ...item, feedback: editedFeedback }
          : item
      )
    );

    // Reset editing state
    setEditingFeedback(null);
    setEditedFeedback("");
  };

  const handleCancelEdit = () => {
    setEditingFeedback(null);
    setEditedFeedback("");
  };

  const handleDeleteFeedback = (index) => {
    setFeedbackToDelete(index);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (feedbackToDelete !== null) {
      setFeedbackList(prevList => prevList.filter((_, i) => i !== feedbackToDelete));
      setIsDeleteModalOpen(false);
      setFeedbackToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setFeedbackToDelete(null);
  };

  const handleHighlightChange = (setting) => {
    setHighlightControls(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const shouldHighlightFeedback = (priority) => {
    switch (priority) {
      case 2: return highlightControls.topPriority;
      case 1: return highlightControls.mediumPriority;
      case 0: return highlightControls.lowPriority;
      default: return false;
    }
  };

  const getHighlightColor = (priority) => {
    switch (priority) {
      case 2:
        return highlightControls.topPriority ? 'bg-red-200' : '';
      case 1:
        return highlightControls.mediumPriority ? 'bg-yellow-200' : '';
      case 0:
        return highlightControls.lowPriority ? 'bg-green-200' : '';
      default:
        return '';
    }
  };

  const handleImageClick = (imageUrl) => {
    setModalImage(imageUrl);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFeedbackHover = (index, boundingBoxes) => {
    if (enableHighlighting) {
      setHoveredFeedbackIndex(index);
    }
  };

  const handleFeedbackLeave = () => {
    setHoveredFeedbackIndex(null);
  };

  const handleAddFeedback = () => {
    setIsAddFeedbackModalOpen(true);
  };


  const renderFeedbackItem = (fb, i) => (
    <div
      key={i}
      className={`group transition-all duration-200 ${shouldHighlightFeedback(fb.priority) ?
          fb.priority === 2 ? 'bg-red-50' :
            fb.priority === 1 ? 'bg-yellow-50' :
              'bg-green-50'
          : ''
        }`}
      onMouseEnter={() => handleFeedbackHover(i, fb.bounding_boxes)}
      onMouseLeave={handleFeedbackLeave}
    >
      {editingFeedback === i ? (
        <div className="p-4">
          <textarea
            value={editedFeedback}
            onChange={(e) => setEditedFeedback(e.target.value)}
            className="w-full p-3 text-sm border border-gray-200 rounded-lg
              focus:ring-2 focus:ring-indigo-500 focus:border-transparent
              bg-white shadow-sm"
            rows="3"
            autoFocus
          />
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => handleSaveFeedback(i)}
              className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg
                hover:bg-indigo-700 transition-colors"
            >
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg
                hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between p-4 group-hover:bg-gray-50">
          <div className="flex gap-3 flex-1">
            <div className="flex-shrink-0 mt-1">
              <span className={`flex h-8 w-8 items-center justify-center rounded-full 
                ${getHighlightColor(fb.priority)} border border-gray-200`}>
                {i + 1}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-gray-700 leading-relaxed">{fb.feedback}</p>
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => handleEditFeedback(fb.feedback, i)}
              className="p-2 bg-transparent text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 
                rounded-lg transition-colors"
              title="Edit feedback"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => handleDeleteFeedback(i)}
              className="p-2 bg-transparent text-gray-500 hover:text-red-600 hover:bg-red-50 
                rounded-lg transition-colors"
              title="Delete feedback"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderHighlightControls = () => (
    <div className="border-t border-gray-100 bg-gray-50">
      <div className="p-4">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-6 bg-white px-4 py-2 rounded-lg border border-gray-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableHighlighting}
                  onChange={(e) => setEnableHighlighting(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-indigo-600 rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Enable Highlighting</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={highlightControls.topPriority}
                  onChange={() => handleHighlightChange('topPriority')}
                  className="form-checkbox h-4 w-4 text-red-600 rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">High</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={highlightControls.mediumPriority}
                  onChange={() => handleHighlightChange('mediumPriority')}
                  className="form-checkbox h-4 w-4 text-yellow-600 rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Medium</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={highlightControls.lowPriority}
                  onChange={() => handleHighlightChange('lowPriority')}
                  className="form-checkbox h-4 w-4 text-green-600 rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Low</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderImageViewer = (response, activePage) => (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
      <div className="relative">
        <img
          src={response?.pages?.[activePage]?.image_url}
          alt={`Page ${activePage + 1}`}
          className="essay-image w-full h-auto object-contain"
          onClick={() => handleImageClick(response?.pages?.[activePage]?.image_url)}
        />

        {enableHighlighting &&
          hoveredFeedbackIndex !== null &&
          response?.pages?.[activePage]?.feedback?.[hoveredFeedbackIndex]?.bounding_boxes && (
            <div
              className="absolute inset-0 pointer-events-none"
            >
              {Object.entries(response.pages[activePage].feedback[hoveredFeedbackIndex].bounding_boxes)
                .map(([key, coords]) => {
                  const xCenter = (coords[0] + coords[2]) / 2;
                  const yCenter = (coords[1] + coords[3]) / 2;
                  const yRadius = Math.abs(coords[3] - coords[1]) * 1.5;

                  const gradientStops = [
                    'rgba(0, 0, 0, 0.2) 0%',
                    `rgba(0, 0, 0, 0.2) ${yCenter - yRadius}px`,
                    `rgba(0, 0, 0, 0.1) ${yCenter - 0.7 * yRadius}px`,
                    `rgba(0, 0, 0, 0.0) ${yCenter - 0.5 * yRadius}px`,
                    `rgba(0, 0, 0, 0.0) ${yCenter}px`,
                    `rgba(0, 0, 0, 0.0) ${yCenter + 0.5 * yRadius}px`,
                    `rgba(0, 0, 0, 0.1) ${yCenter + 0.7 * yRadius}px`,
                    `rgba(0, 0, 0, 0.2) ${yCenter + yRadius}px`,
                    'rgba(0, 0, 0, 0.1) 100%'
                  ];

                  return (
                    <div
                      key={key}
                      className="absolute transition-all duration-200"
                      style={{
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                        background: `
                        linear-gradient(to right, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.0) 20%, rgba(0, 0, 0, 0.0) 80%, rgba(0, 0, 0, 0.2) 100%),
                        linear-gradient(to bottom, ${gradientStops.join(', ')})
                      `,
                        zIndex: 10
                      }}
                    />
                  );
                })}
            </div>
          )}
      </div>

      <div className="border-t border-gray-100">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={handlePrevPage}
            disabled={activePage === 0}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${activePage === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 border border-indigo-600'
              }`}
          >
            Previous
          </button>
          <div className="flex items-center gap-2">
            {response.pages.map((_, index) => (
              <button
                key={index}
                onClick={() => setActivePage(index)}
                className={`flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium transition-colors
                  ${activePage === index
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
                  }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <button
            onClick={() => handleNextPage(response.pages.length)}
            disabled={activePage === response.pages.length - 1}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${activePage === response.pages.length - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 border border-indigo-600'
              }`}
          >
            Next
          </button>
        </div>

        {renderHighlightControls()}
      </div>
    </div>
  );
  const DeleteConfirmationModal = () => (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      onClick={cancelDelete}
    >
      <div
        className="bg-white rounded-lg shadow-lg p-8 max-w-sm w-full mx-6 transform transition-all duration-300 ease-out scale-95 hover:scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-gray-800 text-lg font-medium text-center mb-6">
          Are you sure you want to delete this feedback?
        </p>

        <div className="flex justify-center gap-8">
          <button
            onClick={cancelDelete}
            className="px-8 py-3 text-base text-gray-700 bg-gray-200 rounded-full hover:bg-gray-300 transition duration-200 ease-in-out"
          >
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            className="px-8 py-3 text-base text-white bg-red-600 rounded-full hover:bg-red-700 transition duration-200 ease-in-out"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );


  const AddFeedbackModal = () => {
    // Keep temporary state for feedback text
    const [tempFeedbackText, setTempFeedbackText] = useState(newFeedback.text);

    const handlePriorityChange = (e) => {
      // Update priority while preserving the temporary text
      setNewFeedback(prev => ({
        ...prev,
        text: tempFeedbackText, // Keep the current temporary text
        priority: e.target.value
      }));
    };

    const handleSaveNewFeedback = () => {
      if (!tempFeedbackText || !newFeedback.priority) {
        return; // Don't save if either field is empty
      }

      const feedbackItem = {
        feedback: tempFeedbackText,
        priority: parseInt(newFeedback.priority),
        bounding_boxes: {}
      };

      console.log(feedbackItem, "new feedbackItem");

      setFeedbackList(prevList => [...prevList, feedbackItem]);
      setIsAddFeedbackModalOpen(false);

      // Reset both states
      setNewFeedback({
        text: '',
        priority: ''
      });
      setTempFeedbackText('');
    };

    console.log(feedbackList, "feedbackList");

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={() => setIsAddFeedbackModalOpen(false)}
      >
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4 rounded-t-xl">
            <h3 className="text-lg font-semibold text-white">Add New Feedback</h3>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feedback Text
              </label>
              <textarea
                value={tempFeedbackText}
                onChange={(e) => setTempFeedbackText(e.target.value)}
                className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 
                  focus:ring-indigo-500 focus:border-transparent"
                rows="4"
                autoFocus
                placeholder="Enter your feedback here..."
                style={{
                  direction: 'ltr',
                  textAlign: 'left',
                  unicodeBidi: 'plaintext'
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority Level
              </label>
              <select
                value={newFeedback.priority}
                onChange={handlePriorityChange}
                className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 
                  focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Select priority</option>
                <option value="2">High Priority</option>
                <option value="1">Medium Priority</option>
                <option value="0">Low Priority</option>
              </select>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
            <button
              onClick={() => setIsAddFeedbackModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white 
                border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveNewFeedback}
              disabled={!tempFeedbackText || !newFeedback.priority}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg
                ${(!tempFeedbackText || !newFeedback.priority)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
            >
              Save Feedback
            </button>
          </div>
        </div>
      </div>
    );
  };
  // Function to handle modifying an essay
  const handleModifyEssay = async () => {
    // Create a deep copy of the question responses and update the feedback
    const updatedQuestionResponses = feedBackState.question_responses.map(response => ({
      ...response,
      pages: response.pages.map(page => ({
        ...page,
        feedback: feedbackList  // Include the updated feedback list
      }))
    }));

    const payload = {
      essay_id: "20",
      updated_essay_data: {
        question_responses: updatedQuestionResponses
      }
    };

    console.log('Payload being sent:', payload); // Debug log

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/modifyessay/`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === "success") {
        alert(`Essay ${response.data.essay_id} modified successfully!`);
      } else {
        alert("Error: " + response.data.message);
      }
    } catch (error) {
      console.error("Error modifying essay:", error);
      alert("Error modifying essay: " + error.message);
    }
  };

  const handleSaveAllChanges = () => {
    handleModifyEssay();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-gray-50">
      <div className="max-w-8xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-8 bg-white rounded-2xl p-3 shadow-lg border border-gray-100">
          <div className="flex flex-wrap gap-3">
            {essayData.question_responses.map((_, index) => (
              <button key={index}
                onClick={(e) => handleTabChange(e, index)}
                className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 
                ${activeTab === index
                    ? 'bg-indigo-600 text-white shadow-lg ring-2 ring-indigo-300'
                    : 'bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'
                  }`}
              >
                Question {index + 1}
              </button>
            ))}
          </div>
        </div>

        {essayData.question_responses.map((response, index) => (
          <div key={index} className={`${activeTab !== index ? 'hidden' : ''}`}>
            {/* Question Card - Now at the top, full width */}
            <div className="mb-8 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4">
                <h3 className="text-lg font-semibold text-white  flex items-center gap-2">
                  <span className="p-1">📝</span>
                  Question {index + 1}
                </h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 leading-relaxed">{response.question}</p>
              </div>

            </div>

            <div className="flex flex-col xl:flex-row gap-8">
              <div className="xl:w-2/3">
                {renderImageViewer(response, activePage)}
              </div>

              <div className="xl:w-1/3 space-y-6">
                {response.overall_feedback && (
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <span className="p-1">✨</span>
                        Overall Assessment
                      </h3>
                    </div>
                    <div className="p-6">
                      <ul className="space-y-4">
                        {response.overall_feedback.map((feedback, i) => (
                          <li key={i} className="flex items-start gap-3 bg-green-50 p-4 rounded-lg border border-green-200 shadow-sm transition-transform transform hover:scale-105">
                            <span className="text-green-600 mt-1 text-lg">•</span>
                            <span className="text-sm text-gray-800">{feedback}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-4 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="p-1 bg-white/20 rounded-full">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </span>
                        <h3 className="text-lg font-semibold text-white">Feedback Points</h3>
                      </div>
                      <button
                        onClick={handleAddFeedback}
                        className="flex items-center gap-1 px-3 py-1 bg-white/10 hover:bg-white/20 
                        text-white rounded-md transition-all duration-200 border border-white/20"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span className="text-sm font-medium">Add Feedback</span>
                      </button>
                    </div>
                  </div>

                  <div className="divide-y divide-gray-200">
                    {feedbackList.map((fb, i) =>
                      renderFeedbackItem(fb, i)
                    )}

                    {(!feedbackList || feedbackList.length === 0) && (
                      <div className="p-8 text-center">
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                          <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                          </svg>
                        </div>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No feedback yet</h3>
                        <p className="mt-1 text-sm text-gray-500">Get started by adding some feedback for this page.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Save Changes Button */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={handleSaveAllChanges}
          className="px-6 py-3 bg-indigo-600 text-white rounded-xl shadow-lg
          hover:bg-indigo-700 transition-all duration-200 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
          Save All Changes
        </button>
      </div>

      {/* Modal for Image */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCloseModal}
        >
          <div className="bg-white p-4 rounded-lg max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img src={modalImage} alt="Modal View" className="max-w-full max-h-full" />
          </div>
        </div>
      )}

      {isAddFeedbackModalOpen && <AddFeedbackModal />}
      {isDeleteModalOpen && <DeleteConfirmationModal />}
    </div>
  );
};

export default GradedEssayData;